import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchHandovers, createHandover, acknowledgeHandover, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — D2: shift handover (pass-on log). The outgoing agent records the
// state of play; the incoming agent must read-and-sign before taking over.
const S = {
  en: {
    title: "Shift handover", subtitle: "Pass on the desk — the next shift signs off", back: "Back to board",
    newH: "New handover", shift: "Shift", morning: "Morning", afternoon: "Afternoon", night: "Night",
    arrivals: "Arrivals / VIPs", ooo: "Out-of-order rooms", holds: "Holds / do-not-release",
    complaints: "Open complaints / follow-ups", cash: "Cash float", notes: "Other notes",
    useOoo: "Use current OOO", save: "Post handover", none: "No handovers yet.",
    open: "Awaiting sign-off", ack: "Acknowledged", ackBtn: "I've read this — take over", by: "by", at: "at",
    posted: "Posted", ackedBy: "Signed off by",
  },
  am: {
    title: "የፈረቃ ርክክብ", subtitle: "ዴስኩን አስረክብ — ቀጣዩ ፈረቃ ይፈርማል", back: "ወደ ዳሽቦርድ ተመለስ",
    newH: "አዲስ ርክክብ", shift: "ፈረቃ", morning: "ጠዋት", afternoon: "ከሰዓት", night: "ማታ",
    arrivals: "መግቢያ / ቪአይፒ", ooo: "ከአገልግሎት ውጭ ክፍሎች", holds: "ማቆያ / አትልቀቅ",
    complaints: "ክፍት ቅሬታዎች / ክትትል", cash: "የገንዘብ ቀሪ", notes: "ሌላ ማስታወሻ",
    useOoo: "አሁን ያሉትን OOO ተጠቀም", save: "ርክክብ አስቀምጥ", none: "እስካሁን ርክክብ የለም።",
    open: "ፊርማ በመጠበቅ ላይ", ack: "ተረጋግጧል", ackBtn: "አንብቤዋለሁ — ተረከብ", by: "በ", at: "በ",
    posted: "ተለጠፈ", ackedBy: "የተፈረመው በ",
  },
  om: {
    title: "Dabarsa gaggeeffannaa", subtitle: "Deeskii dabarsi — gaggeeffannaan itti aanu mallatteessa", back: "Gara gabatee deebi'i",
    newH: "Dabarsa haaraa", shift: "Gaggeeffannaa", morning: "Ganama", afternoon: "Waaree booda", night: "Halkan",
    arrivals: "Seentota / VIP", ooo: "Kutaalee tajaajila ala", holds: "Qabannoo / hin gadhiisin",
    complaints: "Komii banaa / hordoffii", cash: "Maallaqa harkaa", notes: "Yaadannoo biraa",
    useOoo: "OOO ammaa fayyadami", save: "Dabarsa maxxansi", none: "Ammaaf dabarsi hin jiru.",
    open: "Mallattoo eegaa", ack: "Mirkanaa'e", ackBtn: "Dubbiseera — fudhadhu", by: "kan", at: "yeroo",
    posted: "Maxxanfame", ackedBy: "Kan mallatteesse",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2", fontSize: 13.5, width: "100%", fontFamily: "inherit" };
const lbl = { fontSize: 12, color: "#5f6b78", display: "block", marginBottom: 3 };
const blank = { shift: "morning", arrivals_vip: "", ooo_rooms: "", holds: "", complaints: "", cash_float: "", notes: "" };

export default function HandoverPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [d, setD] = useState(null);
  const [form, setForm] = useState(blank);
  const [showForm, setShowForm] = useState(false);

  const load = () => fetchHandovers().then(setD).catch(() => setD({ handovers: [], prefill: {} }));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    const body = { ...form, cash_float: form.cash_float === "" ? null : Number(form.cash_float) };
    createHandover(body).then(() => { setForm(blank); setShowForm(false); load(); });
  };
  const ack = (h) => acknowledgeHandover(h.id).then(load);

  const field = (key, rows = 2) => (
    <div style={{ marginBottom: 8 }}>
      <label style={lbl}>{t(key === "arrivals_vip" ? "arrivals" : key === "ooo_rooms" ? "ooo" : key)}</label>
      <textarea style={{ ...inp, resize: "vertical" }} rows={rows} value={form[key]} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  const list = (d && d.handovers) || [];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 40 }}>
      <Link className="muted" to="/front-desk/board" style={{ fontSize: 13, textDecoration: "none" }}>&larr; {t("back")}</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
        </div>
        <button className="btn primary" onClick={() => setShowForm((s) => !s)}>+ {t("newH")}</button>
      </div>

      {showForm && (
        <div style={{ ...CARD, padding: 16, marginTop: 12 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
            <div>
              <label style={lbl}>{t("shift")}</label>
              <select style={{ ...inp, width: 160 }} value={form.shift} onChange={(e) => set("shift", e.target.value)}>
                <option value="morning">{t("morning")}</option>
                <option value="afternoon">{t("afternoon")}</option>
                <option value="night">{t("night")}</option>
              </select>
            </div>
            <div>
              <label style={lbl}>{t("cash")}</label>
              <input style={{ ...inp, width: 130 }} type="number" value={form.cash_float} onChange={(e) => set("cash_float", e.target.value)} />
            </div>
            {d && d.prefill && d.prefill.ooo_rooms && (
              <button className="btn small" onClick={() => set("ooo_rooms", d.prefill.ooo_rooms)}>{t("useOoo")}</button>
            )}
          </div>
          {field("arrivals_vip")}
          {field("ooo_rooms")}
          {field("holds")}
          {field("complaints")}
          {field("notes")}
          <button className="btn primary" onClick={submit}>{t("save")}</button>
        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {list.length === 0 ? <div className="muted" style={{ fontSize: 13, padding: 8 }}>{t("none")}</div> :
          list.map((h) => (
            <div key={h.id} style={{ ...CARD, padding: 14, borderLeft: `4px solid ${h.status === "open" ? "#B8860B" : "#1D7A44"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontWeight: 700 }}>
                  {h.shift ? t(h.shift) : ""} · <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t("posted")} {h.created_at ? h.created_at.slice(0, 16).replace("T", " ") : ""}{h.created_by ? ` · ${h.by || ""} ${h.created_by}` : ""}</span>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", color: h.status === "open" ? "#8A5A0B" : "#1D7A44" }}>
                  {h.status === "open" ? t("open") : t("ack")}
                </span>
              </div>
              <table style={{ width: "100%", fontSize: 13, marginTop: 8, borderCollapse: "collapse" }}>
                <tbody>
                  {[["arrivals", h.arrivals_vip], ["ooo", h.ooo_rooms], ["holds", h.holds], ["complaints", h.complaints], ["notes", h.notes]]
                    .filter(([, v]) => v && String(v).trim())
                    .map(([k, v]) => (
                      <tr key={k}><td style={{ padding: "3px 8px 3px 0", color: "#5f6b78", verticalAlign: "top", whiteSpace: "nowrap" }}>{t(k)}</td><td style={{ padding: "3px 0", whiteSpace: "pre-wrap" }}>{v}</td></tr>
                    ))}
                  {h.cash_float != null && <tr><td style={{ padding: "3px 8px 3px 0", color: "#5f6b78" }}>{t("cash")}</td><td>{formatPrice(h.cash_float, cur)}</td></tr>}
                </tbody>
              </table>
              <div style={{ marginTop: 10 }}>
                {h.status === "open" ? (
                  <button className="btn primary small" onClick={() => ack(h)}>✓ {t("ackBtn")}</button>
                ) : (
                  <span style={{ fontSize: 12.5, color: "#1D7A44" }}>✓ {t("ackedBy")} {h.acknowledged_by || ""}{h.acknowledged_at ? ` · ${h.acknowledged_at.slice(0, 16).replace("T", " ")}` : ""}</span>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
